// src/components/ImageCarousel.js
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import './ImageCarousel.css';

function ImageCarousel({ images }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  // Handle Escape key to close lightbox
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isLightboxOpen) {
        closeLightbox();
      }
    };
    
    if (isLightboxOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isLightboxOpen]);

  // If no images provided, show default
  const imageList = images && images.length > 0 ? images : ['/default-plant.png'];

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? imageList.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === imageList.length - 1 ? 0 : prev + 1));
  };

  const openLightbox = () => {
    setIsLightboxOpen(true);
    setZoomLevel(1);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setZoomLevel(1);
  };

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(prev + 0.5, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(prev - 0.5, 1));
  };

  const handleLightboxPrev = (e) => {
    e.stopPropagation();
    handlePrev();
  };

  const handleLightboxNext = (e) => {
    e.stopPropagation();
    handleNext();
  };

  return (
    <>
      <div className="image-carousel">
        <div className="carousel-main-container">
          {/* Thumbnail Gallery on Left */}
          {imageList.length > 1 && (
            <div className="carousel-thumbnails">
              {imageList.map((img, idx) => (
                <button
                  key={idx}
                  className={`thumbnail-button ${idx === currentIndex ? 'active' : ''}`}
                  onClick={() => setCurrentIndex(idx)}
                  aria-label={`View image ${idx + 1}`}
                >
                  <img 
                    src={img} 
                    alt={`Thumbnail ${idx + 1}`}
                    className="thumbnail-image"
                  />
                </button>
              ))}
            </div>
          )}
          
          {/* Main Image Container */}
          <div className="carousel-container">
            {imageList.length > 1 && (
              <button className="carousel-arrow left" onClick={handlePrev} aria-label="Previous image">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M15 18L9 12L15 6" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
            <img 
              src={imageList[currentIndex]} 
              alt={`Product view ${currentIndex + 1}`}
              className="carousel-image"
              onClick={openLightbox}
              style={{ cursor: 'pointer' }}
            />
            {imageList.length > 1 && (
              <button className="carousel-arrow right" onClick={handleNext} aria-label="Next image">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path d="M9 18L15 12L9 6" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox Modal - Rendered in a Portal */}
      {isLightboxOpen && ReactDOM.createPortal(
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            {/* Close Button */}
            <button className="lightbox-close" onClick={closeLightbox} aria-label="Close">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                <path d="M18 6L6 18M6 6l12 12" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>

            {/* Image Container */}
            <div className="lightbox-image-container">
              <img 
                src={imageList[currentIndex]} 
                alt={`Product view ${currentIndex + 1}`}
                className="lightbox-image"
                style={{ transform: `scale(${zoomLevel})` }}
              />
            </div>

            {/* Navigation Arrows */}
            {imageList.length > 1 && (
              <>
                <button className="lightbox-arrow left" onClick={handleLightboxPrev} aria-label="Previous image">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M15 18L9 12L15 6" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <button className="lightbox-arrow right" onClick={handleLightboxNext} aria-label="Next image">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                    <path d="M9 18L15 12L9 6" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </>
            )}

            {/* Zoom Controls */}
            <div className="lightbox-zoom-controls">
              <button onClick={handleZoomOut} disabled={zoomLevel <= 1} aria-label="Zoom out">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="11" cy="11" r="8" stroke="#fff" strokeWidth="2"/>
                  <path d="M8 11h6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M21 21l-4.35-4.35" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
              <span className="zoom-level">{Math.round(zoomLevel * 100)}%</span>
              <button onClick={handleZoomIn} disabled={zoomLevel >= 3} aria-label="Zoom in">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <circle cx="11" cy="11" r="8" stroke="#fff" strokeWidth="2"/>
                  <path d="M8 11h6M11 8v6" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                  <path d="M21 21l-4.35-4.35" stroke="#fff" strokeWidth="2" strokeLinecap="round"/>
                </svg>
              </button>
            </div>

            {/* Image Counter */}
            {imageList.length > 1 && (
              <div className="lightbox-counter">
                {currentIndex + 1} / {imageList.length}
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

export default ImageCarousel;
